import { Component, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { BbTableService, BbTableSelectOption, TableRow } from '../../../services/bb-table.service';
import { FilterSetService } from '../../../services/filter-set.service';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { Publication } from '../../../interfaces/publication';
import { PublicationService } from '../../../services/publication.service';
import { DateRange } from '../../../interfaces/dateRange';
import { SapiQueryResponse, SapiEntity } from '../../../services/sapi.service';
import { AdunitStatsService } from '../../../services/sapi/adunit-stats.service';
import { AdServicesService } from '../../../services/ad-services.service';
import { AdunitStats } from '../../../shared/models/sapi/adunit-stats.model';
import dayjs from 'dayjs';
import { HumanReadableNumber } from '../../../shared/utils/humanReadableNumber.util';
import { ClipboardService } from 'ngx-clipboard';
import { LineItem } from '../lineitem/lineitem-details.component';
import { Playout } from '../../../interfaces/playout';
import { bbDeepCopy } from '../../../shared/utils/bbDeepCopy.util';

export interface Adunit extends SapiEntity {
	title?: string;
	code?: string;
	positionType?: string;
	assignedLineitems?: string[];
	lineitems?: LineItem[];
	playout?: AdunitPlayout;
	adunits?: Adunit[];
	adunitsVMAP?: Adunit[];
	adunitsPreroll?: Adunit[];
	adunitsPostroll?: Adunit[];
	playoutCode?: string;
	description?: string;
	clipId?: string;
	autoMute?: string;
	outstream?: string;
	hidden?: boolean;
}

export interface AdunitPlayout extends Playout {
	ctaText?: string;
	ctaMidplay?: string;
	ctaMidplayPosition?: string;
	ctaExitScreen?: string;
	ctaBackgroundColor?: string;
	ctaHighlightColor?: string;
	ctaExitScreenShade?: string;
	eventHandlers?: any[];
	stickyMode?: string;
	domFloatElement?: string;
	domElementAlignment?: string;
	floatingWidth?: string;
	minimumFloatWidth?: string;
	alwaysFloat?: string;
	showFloatingCloseButton?: string;
	disableFloatAnimation?: string;
	height?: string;
	maxWidth?: string;
	minClipDurationPreroll?: string;
	minClipDurationPostroll?: string;
	commercialPauseButton?: string;
	commercialMuteButton?: string;
	textCommercialTimeRemaining?: string;
	allowCustomClickLayer?: string;
	textAbovePlayer?: string;
	textCommercialSkip?: string;
	cmpTimeout?: string;
}

@Component({
	selector: 'adunit-list',
	templateUrl: './adunit-list.component.html',
	styleUrls: ['./adunit-list.component.scss'],
	providers: [BbTableService, FilterSetService],
})

export class AdunitListComponent extends AbstractListComponent implements AfterViewInit {
	// TODO: Implement minDate
	minDate: Date;
	statsDate: DateRange;
	stats: any[];
	statsObject: any;
	publication: Publication;
	newDataFromDate: Date;
	expandedAdUnits: boolean;
	assignedLineItems: any;
	allowCustomPreset: boolean;
	lastStatsDate: DateRange;
	downloadReport: boolean;
	rows: TableRow[];
	rootPermission: boolean;

	constructor(
		protected bbTableService: BbTableService,
		protected filterSetService: FilterSetService,
		protected publicationService: PublicationService,
		protected adunitStatsService: AdunitStatsService,
		protected clipboardService: ClipboardService,
		public adServicesService: AdServicesService,
		private changeDetector: ChangeDetectorRef) {

		super(bbTableService, filterSetService);

		this.assignedLineItems = {};
		this.allowCustomPreset = true;

		this.listConfiguration = {
			name: 'adunit',
			basePath: '/adservices/adunits/',
			activePath: '/adservices/adunits',
			entityTranslation: this.translate.instant('ADUNIT').toLowerCase(),
			entitiesTranslation: this.translate.instant('ADUNITS').toLowerCase()
		};

		this.filterConfig = {
			search: true,
			filters: [
				{
					property: 'title',
					label: this.translate.instant('TITLE'),
					type: 'text'
				},
				{
					property: 'code',
					label: this.translate.instant('CODE'),
					type: 'text'
				},
				{
					property: 'positionType',
					label: this.translate.instant('FORMAT'),
					type: 'select'
				},
				{
					property: 'status',
					label: this.translate.instant('STATUS'),
					type: 'select'
				},
				{
					property: 'createdBy',
					label: this.translate.instant('CREATED_BY'),
					type: 'select'
				}, {
					property: 'updatedBy',
					label: this.translate.instant('UPDATED_BY'),
					type: 'select'
				},
				{
					property: 'createddate',
					label: this.translate.instant('CREATED_DATE'),
					type: 'date'
				},
				{
					property: 'updateddate',
					label: this.translate.instant('UPDATED_DATE'),
					type: 'date'
				}
			],
			presets: [
				{
					label: this.translate.instant('ALL'),
					value: 'all',
					filterSet: [],
				},
				{
					label: this.translate.instant('ACTIVE'),
					value: 'published',
					filterSet: [{
						filters: [{
							type: 'adunit',
							field: 'status',
							operator: 'is',
							value: 'published'
						}]
					}],
				},
				{
					label: this.translate.instant('INACTIVE'),
					value: 'draft',
					filterSet: [{
						filters: [{
							type: 'adunit',
							field: 'status',
							operator: 'is',
							value: 'draft'
						}]
					}],
				},
				{
					label: this.translate.instant('PREROLL'),
					value: 'preroll',
					filterSet: [{
						filters: [{
							type: 'adunit',
							field: 'positionType',
							operator: 'is',
							value: 'preroll'
						}]
					}],
				},
				{
					label: this.translate.instant('POSTROLL'),
					value: 'postroll',
					filterSet: [{
						filters: [{
							type: 'adunit',
							field: 'positionType',
							operator: 'is',
							value: 'postroll'
						}]
					}],
				},
				{
					label: this.translate.instant('OUTSTREAM'),
					value: 'inarticle',
					filterSet: [{
						filters: [{
							type: 'adunit',
							field: 'positionType',
							operator: 'is',
							value: 'inarticle'
						}]
					}],
				}
			]
		};

		this.newDataFromDate = new Date('2018-07-16T14:00:00.000Z');

		this.subscriptions.add(
			this.publicationService.getData().subscribe((publication: Publication) => {
				if (publication) {
					this.publication = publication;
				}
			})
		);

		this.subscriptions.add(
			this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
				this.rows = rows;
				this.lastStatsDate = null;
				this.updateAdunitStatsRows();
			})
		);

		this.subscriptions.add(
			this.bbTableService.offsetChangedObservable.subscribe((offset: number) => {
				this.assignedLineItems = [];
			})
		);

		this.authenticationService.hasPermission('root').subscribe((hasRight: boolean) => this.rootPermission = hasRight);

		this.bbTableService
			.setSelect(BbTableSelectOption.none)
			.setLimit(15)
			.sort('id', 'desc')
			.linkToUrl();
	}

	redirectToNew(type: string, event: MouseEvent) {
		this.router.navigate(['/adservices/adunits', 'new-' + type, 'general']);
	}

	copyAdunit(adunit: Adunit) {
		const type = 'new-' + adunit.positionType;
		this.router.navigate(['/adservices/adunits', type, 'general'], { queryParams: { fromId: adunit.id, positionType: adunit.positionType } });
	}

	ngAfterViewInit() {
		this.changeDetector.detectChanges();
	}

	updateAdunitStatsRows() {
		if (this.rows
			&& (!this.lastStatsDate || this.lastStatsDate.startDate !== this.statsDate.startDate || this.lastStatsDate.endDate !== this.statsDate.endDate)) {
			this.rows.forEach((row: any) => {
				const adunitParams: any = { ...this.statsObject };
				adunitParams.adunit = row.data.code;

				if (row.data.positionType === 'inarticle') {
					row.outstream = true;
				}

				this.adunitStatsService.get('', adunitParams).subscribe((adunitStats: AdunitStats) => {
					if (adunitStats) {
						row.inview = HumanReadableNumber.transform(adunitStats.inview) + this.compareNumbers(adunitStats.inview, adunitStats.inview_compare);
						row.inventory = HumanReadableNumber.transform(adunitStats.inits) + this.compareNumbers(adunitStats.inits, adunitStats.inits_compare);
						row.views = HumanReadableNumber.transform(adunitStats.views) + this.compareNumbers(adunitStats.views, adunitStats.views_compare);
						row.placementRate = HumanReadableNumber.transform(adunitStats.placementRate)
							+ '%' + this.compareNumbers(adunitStats.placementRate, adunitStats.placementRate_compare);
					} else {
						row.inview = 0 + ' (n/a)';
						row.inventory = 0 + '(n/a)';
						row.views = 0 + '(n/a)';
						row.placementRate = 0 + '(n/a)';
					}
				});
			});

			this.lastStatsDate = bbDeepCopy(this.statsDate);
		}
	}

	downloadFromUrl() {
		this.downloadReport = true;
		let url = this.locationService.server + '/sapi/adstats/download?q=*%3A*';
		let filename = 'adstats-download';
		if (this.statsObject) {
			Object.keys(this.statsObject).forEach((key: string) => {
				url += '&' + key + '=' + this.statsObject[key];
			});
			if (this.statsObject.start) {
				filename += '-' + this.statsObject.start;
			}
			if (this.statsObject.end) {
				filename += '-' + this.statsObject.end;
			}
		}
		this.sapiService.downloadFromUrl(url, filename).subscribe(() => this.downloadReport = false);
	}

	updateLineItemStatsRows() {
		if (this.rows && this.rows.length) {
			const expandedRows = this.rows.filter((row: TableRow) => row.expanded === true);
			this.expandRow(expandedRows, true);
		}
	}

	datesChanged(dateRange: DateRange) {
		this.statsDate = dateRange;

		const statsParams: any = {
			start: this.statsDate.startDate.toISOString(),
			end: this.statsDate.endDate.toISOString(),
		};

		// when requesting stats < 1 day, compare with previous day same time period (other cases handled by backend)
		if (Math.abs(dayjs(statsParams.start).diff(statsParams.end, 'hours')) <= 24) {
			statsParams.compare_start = dayjs(statsParams.start).subtract(1, 'days').toISOString();
			statsParams.compare_end = dayjs(statsParams.end).subtract(1, 'days').toISOString();
		}

		this.statsObject = statsParams;

		this.updateAdunitStatsRows();
		this.updateLineItemStatsRows();
	}

	compareNumbers(numberInput: number, numberCompare: number) {
		let comparison = '';
		if (numberInput > 0 && numberCompare > 0) {
			const percentualDifference = ((numberInput / numberCompare) - 1) * 100;
			if (percentualDifference < 100 && percentualDifference > -100) {
				comparison = percentualDifference.toFixed(1);
			} else {
				comparison = percentualDifference.toFixed(0);
			}
			let color = 'red';
			if (percentualDifference > 0) {
				color = 'green';
			}
			comparison = ' <span class="' + color + '"> (' + comparison + '%)</span>';
		} else {
			comparison = ' <span class="grey"> (n/a)</span>';
		}
		return comparison;
	}

	expandAdunits(expand: boolean) {
		this.expandedAdUnits = expand;
		this.expandRow(this.rows, expand);
	}

	expandRow(tableRow: TableRow | TableRow[], expand: boolean) {
		let rows: TableRow[] = [];
		const lineItemIds: string[] = [];

		if (Array.isArray(tableRow)) {
			rows = tableRow;
		} else {
			rows = [tableRow];
		}

		rows.forEach((row: TableRow) => {
			row.expanded = expand;
			if (expand && row.data.assignedLineitems && row.data.assignedLineitems.length) {
				row.data.assignedLineitems.map((lineItemId: string) => {
					lineItemIds['id:' + lineItemId] = row.data.code;
				});
			}
		});

		for (let i = 0; i < Object.keys(lineItemIds).length; i += 50) {
			const lineItems = Object.keys(lineItemIds).slice(i, (i + 1) * 50);

			const params = {
				limit: lineItems.length * 100,
				q: lineItems.join(' OR '),
				payload: JSON.stringify(this.statsObject)
			};

			this.sapiService.getUrl('/lineitem', params).subscribe((sapiQueryResponse: SapiQueryResponse) => {
				if (sapiQueryResponse && sapiQueryResponse.items && sapiQueryResponse.items.length) {
					sapiQueryResponse.items.forEach((item: SapiEntity) => {
						const lineItem: LineItem = item as LineItem;

						const lineItemParams = { ...this.statsObject };
						const adUnitCode = lineItemIds['id:' + lineItem.id];
						lineItemParams.adunit = adUnitCode;
						lineItemParams.lineitem = lineItem.code;

						this.assignedLineItems[lineItem.id] = {
							id: lineItem.id,
							title: lineItem.title,
							timeout: lineItem.timeout
						};

						this.adunitStatsService.get('', lineItemParams).subscribe((adunitStats: AdunitStats) => {
							if (adunitStats) {
								this.assignedLineItems[lineItem.id].inventory = HumanReadableNumber.transform(adunitStats.inits)
									+ this.compareNumbers(adunitStats.inits, adunitStats.inits_compare);
								this.assignedLineItems[lineItem.id].views = HumanReadableNumber.transform(adunitStats.views)
									+ this.compareNumbers(adunitStats.views, adunitStats.views_compare);
								this.assignedLineItems[lineItem.id].fillRate = HumanReadableNumber.transform(adunitStats.fillrate) + '%'
									+ this.compareNumbers(adunitStats.fillrate, adunitStats.fillrate_compare);
								this.assignedLineItems[lineItem.id].clickthroughRate = HumanReadableNumber.transform(adunitStats.clickrate) + '%'
									+ this.compareNumbers(adunitStats.clickrate, adunitStats.clickrate_compare);
							} else {
								this.assignedLineItems[lineItem.id].inventory = 0 + ' (n/a)';
								this.assignedLineItems[lineItem.id].views = 0 + '(n/a)';
								this.assignedLineItems[lineItem.id].fillRate = 0 + '(n/a)';
								this.assignedLineItems[lineItem.id].clickthroughRate = 0 + '(n/a)';
							}
						});
					});
				}
			});
		}
	}

	copyEmbedCode(adunitCode: string) {
		const embedcode = '<script type="text/javascript" src="https://' + this.publicationService.mainrollBaseUrl + '/a/' + adunitCode + '.js"></script>';
		this.clipboardService.copyFromContent(embedcode);
		this.overlayService.createToast('Copied embed code to clipboard', 'clipboard');
	}
}
